import React, { ButtonHTMLAttributes, FC, ReactNode, useId } from "react";

export interface TripTileProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "value"> {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  /** A second, muted line under the value. */
  sub?: ReactNode;
  /** Nothing chosen yet: the value is a prompt, drawn muted. */
  empty?: boolean;
  /** This tile's picker is open: the line turns into the accent. */
  active?: boolean;
  /** Something at the right end of a wide tile ("Free"). */
  trailing?: ReactNode;
  /**
   * `field`: a hairline field with its label above it (the trip card). `chip`: the compact one the
   * scrolled header bar uses.
   */
  layout?: "field" | "chip";
}

/**
 * 1NOW-11176: a large tappable trip field for the Design B car page. The pattern is the trip fields of
 * turo.com and sixt.com: a small label above, a 1px hairline field with an 8px radius under it, a thin
 * line icon, the value in the text colour. The open field takes the tenant's primary as its line.
 * The three pickers draw their trigger with this, so they read as one family.
 */
const TripTile: FC<TripTileProps> = ({
  icon,
  label,
  value,
  sub,
  empty = false,
  active = false,
  trailing,
  layout = "field",
  className = "",
  ...rest
}) => {
  const id = useId();

  if (layout === "chip") {
    return (
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={active}
        {...rest}
        className={`inline-flex min-w-0 items-center gap-2 px-3 py-2 text-left text-sm font-medium text-text-color transition-colors ${
          active ? "bg-primary-brand/10" : "hover:bg-text-color/5"
        } ${className}`}
      >
        <span className="shrink-0 text-text-color/60">{icon}</span>
        <span className="sr-only">{label}: </span>
        <span className={`min-w-0 truncate ${empty ? "text-text-color/60" : ""}`}>{value}</span>
      </button>
    );
  }

  return (
    <div className={className}>
      <label id={`${id}-label`} htmlFor={`${id}-button`} className="mb-1.5 block cursor-pointer text-xs font-medium text-text-color/70">
        {label}
      </label>
      <button
        id={`${id}-button`}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={active}
        aria-labelledby={`${id}-label ${id}-value`}
        {...rest}
        className={`flex min-h-[48px] w-full items-center gap-3 rounded-lg border bg-card-color px-3 py-2 text-left transition-colors disabled:cursor-not-allowed ${
          active ? "border-primary-brand ring-1 ring-primary-brand" : "border-text-color/20 hover:border-text-color/40"
        }`}
      >
        <span className="shrink-0 text-text-color/60">{icon}</span>
        <span id={`${id}-value`} className="block min-w-0 flex-1">
          <span className={`block truncate text-[15px] ${empty ? "text-text-color/50" : "font-medium text-text-color"}`}>
            {value}
          </span>
          {sub && <span className="block truncate text-xs text-text-color/60">{sub}</span>}
        </span>
        {trailing && <span className="shrink-0 text-xs text-text-color/60">{trailing}</span>}
      </button>
    </div>
  );
};

export default TripTile;
